import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { SettingsModalLogic } from '../logic'
import type { SettingsModalDeps } from '../types'
import Modal from '~common/components/modal'
import { useAppContext } from '~/app-context'
import { Button } from '~common/components/button'
import { LoadingIndicator } from '~common/components/loading'
import { TelegramSection } from './sections/telegram-section'
import { EmailForwardingSection } from './sections/email-forwarding-section'
import { CompanyInfoSection } from './sections/company-info-section'
import { DateFormatSection } from './sections/date-format-section'
import { LanguageSection } from './sections/language-section'
import { PluginSettingsSection } from './sections/plugin-settings-section'
import { DangerZoneSection } from './sections/danger-zone-section'
import { ErrorMessage } from './shared-styles'

export interface SettingsModalProps extends SettingsModalDeps {
    isOpen: boolean
    onOpen: () => void
}

const SECTION_MAP: Record<string, string> = {
    'booking-categories': 'plugin-settings',
    'plugin-settings': 'plugin-settings',
    'company-info': 'company-info',
    'date-format': 'date-format',
    'email-forwarding': 'email-forwarding',
    telegram: 'telegram',
    language: 'language',
    'danger-zone': 'danger-zone',
}

export const SettingsModal: React.FC<SettingsModalProps> = (props) => {
    const { t } = useTranslation('settings')
    const appContext = useAppContext()
    const { logic, state } = useLogic(SettingsModalLogic, {
        ...props,
        ...appContext,
    })

    const [showAllSettings, setShowAllSettings] = React.useState(false)

    const SECTION_TITLES: Record<string, string> = {
        telegram: t('sections.telegram.title'),
        'email-forwarding': t('sections.emailForwarding.title'),
        'company-info': t('sections.companyInfo.title'),
        'date-format': t('sections.dateFormat.title'),
        language: t('sections.language.title'),
        'plugin-settings': t('sections.pluginSettings.title'),
        'danger-zone': t('sections.dangerZone.title'),
    }

    const SECTION_DESCRIPTIONS: Record<string, string> = {
        telegram: t('sections.telegram.description'),
        'email-forwarding': t('sections.emailForwarding.description'),
        'company-info': t('sections.companyInfo.description'),
        'date-format': t('sections.dateFormat.description'),
        language: t('sections.language.description'),
        'plugin-settings': t('sections.pluginSettings.description'),
        'danger-zone': t('sections.dangerZone.description'),
    }

    const targetSection = props.openSection
        ? SECTION_MAP[props.openSection] || null
        : null
    const isSpecificView = !showAllSettings && !!targetSection
    const shouldShowAll = showAllSettings || !targetSection

    const shouldShowSection = (sectionId: string) => {
        return shouldShowAll || sectionId === targetSection
    }

    const getModalTitle = () => {
        if (shouldShowAll) {
            return t('title')
        }
        if (
            targetSection === 'plugin-settings' &&
            props.openSection === 'booking-categories'
        ) {
            const pluginId = logic.getPluginsToDisplay()[0]
            if (pluginId) {
                return logic.getPluginTitle(pluginId)
            }
        }
        return SECTION_TITLES[targetSection || ''] || t('title')
    }

    const getModalDescription = () => {
        if (shouldShowAll) {
            return null
        }
        if (
            targetSection === 'plugin-settings' &&
            props.openSection === 'booking-categories'
        ) {
            const pluginId = logic.getPluginsToDisplay()[0]
            if (pluginId) {
                return logic.getPluginDescription(pluginId)
            }
        }
        return SECTION_DESCRIPTIONS[targetSection || ''] || null
    }

    const modalFooter = (
        <ModalFooter>
            <Button
                label={t('cancel')}
                onClick={props.onClose}
                variant="ghost"
                size="medium"
            />
            <Button
                label={t('save')}
                onClick={() => logic.saveSettings()}
                disabled={state.isSaving}
                variant="primary"
                size="medium"
            />
        </ModalFooter>
    )

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            footer={modalFooter}
            size="medium"
            enableScroll
            position="contain"
            showCloseButton={false}
        >
            {state.loadState === 'running' && (
                <LoadingContainer>
                    <LoadingIndicator />
                </LoadingContainer>
            )}
            {state.loadState === 'success' && (
                <ModalInnerContainer>
                    <ModalHeader>
                        <ButtonSection position="left">
                            {targetSection && !showAllSettings && (
                                <Button
                                    onClick={() => setShowAllSettings(true)}
                                    variant="ghost"
                                    size="medium"
                                    label={t('allSettings')}
                                    icon="arrow-left"
                                    iconPosition="left"
                                />
                            )}
                        </ButtonSection>
                        <ModalTitle>{t('title')}</ModalTitle>
                        <ButtonSection position="right">
                            {modalFooter}
                        </ButtonSection>
                    </ModalHeader>

                    {isSpecificView && getModalDescription() && (
                        <SpecificViewContainer>
                            <SpecificViewTitle>
                                {getModalTitle()}
                            </SpecificViewTitle>
                            <SpecificViewSubTitle>
                                {getModalDescription()}
                            </SpecificViewSubTitle>
                        </SpecificViewContainer>
                    )}

                    <ModalBody>
                        {shouldShowSection('telegram') && (
                            <TelegramSection
                                logic={logic}
                                expanded={state.telegramExpanded}
                                onToggle={logic.toggleTelegramExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {shouldShowSection('email-forwarding') && (
                            <EmailForwardingSection
                                logic={logic}
                                expanded={state.emailForwardingExpanded}
                                onToggle={logic.toggleEmailForwardingExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {shouldShowSection('company-info') && (
                            <CompanyInfoSection
                                logic={logic}
                                state={{ countryCode: state.countryCode }}
                                expanded={state.companyInfoExpanded}
                                onToggle={logic.toggleCompanyInfoExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {shouldShowSection('date-format') && (
                            <DateFormatSection
                                logic={logic}
                                state={{ dateFormat: state.dateFormat }}
                                expanded={state.dateFormatExpanded}
                                onToggle={logic.toggleDateFormatExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {shouldShowSection('language') && (
                            <LanguageSection
                                logic={logic}
                                state={{ language: state.language }}
                                expanded={state.languageExpanded}
                                onToggle={logic.toggleLanguageExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {shouldShowSection('plugin-settings') &&
                            logic.getPluginsToDisplay().length > 0 && (
                                <PluginSettingsSection
                                    logic={logic}
                                    state={{
                                        countryCode: state.countryCode,
                                        pluginSettingsExpanded:
                                            state.pluginSettingsExpanded,
                                        expandedPlugins: state.expandedPlugins,
                                    }}
                                    expanded={state.pluginSettingsExpanded}
                                    onToggle={
                                        logic.togglePluginSettingsExpanded
                                    }
                                    showTitleInSection={shouldShowAll}
                                    isSpecificView={logic.isShowingSpecificPlugin()}
                                />
                            )}

                        {shouldShowSection('danger-zone') && (
                            <DangerZoneSection
                                logic={logic}
                                state={{
                                    dangerZoneExpanded:
                                        state.dangerZoneExpanded,
                                    showDeleteConfirm: state.showDeleteConfirm,
                                    deleteConfirmationText:
                                        state.deleteConfirmationText,
                                    isDeleting: state.isDeleting,
                                    deleteError: state.deleteError,
                                }}
                                expanded={state.dangerZoneExpanded}
                                onToggle={logic.toggleDangerExpanded}
                                showTitleInSection={shouldShowAll}
                            />
                        )}

                        {state.error && (
                            <ErrorMessage>{state.error}</ErrorMessage>
                        )}
                    </ModalBody>
                </ModalInnerContainer>
            )}
        </Modal>
    )
}

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    width: 100%;
`

const ModalTitle = styled.h1`
    margin: 0;
    z-index: 1;
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    flex: 1;
    text-align: center;
`
const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
`

const ModalFooter = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[3]};
    justify-content: flex-end;
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
    justify-content: center;
`

const ModalInnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    justify-content: flex-start;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    padding: ${({ theme }) => theme.spacing[4]};
`

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
`

const ButtonSection = styled.div<{ position: 'left' | 'right' }>`
    display: flex;
    width: 40%;
    justify-content: ${({ position }) =>
        position === 'left' ? 'flex-start' : 'flex-end'};
`

const SpecificViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    background: ${({ theme }) => theme.colors.background.bg2};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin: 0 ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[4]};
    box-sizing: border-box;
`

const SpecificViewTitle = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
`

const SpecificViewSubTitle = styled.span`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    color: ${({ theme }) => theme.colors.text.tertiary};
`
